<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3>Ingredients</h3>
                <a href="#" @click.prevent="onNewIngredient">
                    <span>New Ingredient</span> <i class="fas fa-plus-circle" />
                </a>
            </b-col>
        </b-row>
        <b-row v-if="showForm">
            <b-col cols="2">
                <label>Drug Source</label>
                <div>{{ drugSourceFormatter(this.upsertDrugSource) }}</div>
            </b-col>
            <b-col cols="3">
                <label>Drug Name</label>
                <div>{{ this.upsertDrugName || 'Loading...' }}</div>
            </b-col>
            <b-col cols="40">
                <fg-input v-model.number="upsertQuantity" :no-wheel="true" label="Quantity" placeholder="0.00"
                    type="number" />
            </b-col>
            <b-col cols="40">
                <fg-input v-model="upsertUnitOfMeasure" label="Unit Of Measure" type="string" />
            </b-col>
            <b-col cols="2">
                <b-form-group label="Legend">
                    <b-form-select v-model="upsertLegendIndicator" :options="legendOptions" />
                </b-form-group>
            </b-col>
            <b-col cols="12">
                <fg-input v-model="upsertIngredientNote" label="Ingredient Note" type="string" />
            </b-col>
            <template v-if="showActivityFactor">
                <b-col cols="12">
                    <b-form-checkbox v-model="upsertApplyActivityFactor" switch class="mb-2">
                        Apply activity factor
                        <small class="text-muted d-block">
                            Adjust this ingredient's weigh-out for the potency of the lot used (from its Certificate of
                            Analysis). Leave off for bases, excipients and vehicles.
                        </small>
                    </b-form-checkbox>
                </b-col>
                <b-col cols="12" md="5">
                    <b-form-group label="Manual conversion factor (replaces COA-derived adjustment)"
                        :description="fixedFactorHelpText">
                        <b-input type="number" step="0.0001" min="0" :value="upsertFixedConversionFactor"
                            :disabled="!upsertApplyActivityFactor" placeholder="e.g. 1.033"
                            @input="onFixedFactorInput" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="7" class="pt-md-4">
                    <div v-if="upsertApplyActivityFactor && fixedFactorPreview" class="small">
                        <b-icon icon="info-circle" />
                        Weigh-out = formula quantity × {{ upsertFixedConversionFactor }} (activity factor
                        {{ fixedFactorPreview }}). <strong>Per-lot COA values will be ignored for this ingredient.</strong>
                    </div>
                    <div v-else-if="upsertApplyActivityFactor && fixedFactorInvalid" class="small text-danger">
                        <b-icon icon="exclamation-triangle-fill" /> {{ fixedFactorInvalidText }}
                    </div>
                    <div v-else-if="upsertApplyActivityFactor" class="small text-muted">
                        <b-icon icon="info-circle" /> Weigh-out will be adjusted from the selected lot's COA at fill time.
                    </div>
                </b-col>
            </template>
            <b-col cols="12">
                <b-button variant="success" @click="submitUpsert">Continue</b-button>
                <b-button @click="onCancelClick">Cancel</b-button>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table id="ingredients-table" ref="ingredients-table" :fields="fields" :items="ingredients"
                    primary-key="id">
                    <template #cell(drugName)="data">
                        <router-link :to="linkForItem(data.item)">
                            {{ data.value }}
                        </router-link>
                    </template>
                    <template #cell(activityFactor)="data">
                        <template v-if="data.item.applyActivityFactor">
                            <b-badge variant="info">Applied</b-badge>
                            <div v-if="data.item.fixedConversionFactor != null" class="small text-muted"
                                v-b-tooltip="'Per-lot COA values are ignored for this ingredient'">
                                Fixed × {{ data.item.fixedConversionFactor }} (COA ignored)
                            </div>
                            <div v-else class="small text-muted">From lot COA</div>
                        </template>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right">
                            <!-- Order carries meaning for geometric dilution, so it is set here
                                 rather than left to whatever the identity column yields. -->
                            <a href="#" :class="{ disabled: data.index === 0 }"
                                @click.prevent="moveIngredient(data.item, -1)">
                                <b-icon v-b-tooltip="'Move up'" icon="arrow-up" />
                            </a>
                            <a href="#" class="mx-1" :class="{ disabled: data.index === ingredients.length - 1 }"
                                @click.prevent="moveIngredient(data.item, 1)">
                                <b-icon v-b-tooltip="'Move down'" icon="arrow-down" />
                            </a>
                            <a href="#" @click.prevent="updateIngredient(data.item)">
                                <b-icon v-b-tooltip="'Edit'" icon="pencil-square" />
                            </a>
                            <a href="#" @click.prevent="deleteConfirmDialog(data.item)">
                                <b-icon v-b-tooltip="'Remove'" class="mx-1" icon="x-circle-fill" variant="danger" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal id="IngredientDrugSelect" hide-footer hide-header size="xl">
            <IngredientSelectModalComponent :current-compound-id="compoundId" @cancel="onIngredientCanceled" @select="onIngredientSelected($event)" />
        </b-modal>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import IngredientSelectModalComponent from '@/components/Compound/IngredientSelectModalComponent.vue';
import { CompoundIngredients } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import Axios, { AxiosError } from "axios";
import { BTable } from "bootstrap-vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { ACTIVITY_FACTOR_SETTINGS_OFF, ActivityFactorSettings, loadActivityFactorSettings } from '@/util/activityFactorApi';
import {
    describeFixedFactorBand,
    fixedFactorActivityFactor,
    formatActivityFactor,
    isFixedFactorUsable,
    toNumberOrNull,
} from '@/util/activityFactor';

@Component({
    name: "IngredientComponent",
    components: {
        IngredientSelectModalComponent
    }
})
export default class IngredientComponent extends Vue {

    @Prop() private compoundId!: number;
    @Prop() private ingredients!: CompoundIngredients[];

    @Ref('ingredients-table') private ingredientsTable!: BTable;

    private showForm: boolean = false;
    private upsertId: number = 0;
    private upsertDrugSource: number = 0;
    private upsertDrugId: number = 0;
    private upsertDrugName: string = "";
    private upsertQuantity: number = 0;
    private upsertUnitOfMeasure: string = "";
    private upsertLegendIndicator: string | null = null;
    private upsertIngredientNote: string = "";
    private upsertApplyActivityFactor: boolean = false;
    private upsertFixedConversionFactor: number | null = null;
    private upsertIngredientRow: CompoundIngredients = new CompoundIngredients;
    private alreadyShipped: boolean = false;
    private drugNamesCache: Map<string, string> = new Map();
    private ndcCache: Map<string, string> = new Map();
    private loadingDrugNames: Set<string> = new Set();
    private activityFactorSettings: ActivityFactorSettings = ACTIVITY_FACTOR_SETTINGS_OFF;

    async created() {
        this.activityFactorSettings = await loadActivityFactorSettings();
    }

    get showActivityFactor(): boolean {
        return this.activityFactorSettings.enabled;
    }

    get fields() {
        const fields: any[] = [
            // "id",
            { key: "drugSource", formatter: this.drugSourceFormatter },
            { key: "drugName", formatter: this.drugNameFormatter },
            { key: "ndc", label: "NDC", formatter: this.ndcFormatter },
            "quantity",
            "unitOfMeasure",
            { key: "legendIndicator", label: "Legend", formatter: this.legendFormatter },
            { key: "ingredientNote", label: "Note" },
            { key: "activityFactor", label: "Activity Factor", hidden: !this.showActivityFactor },
            { key: "actions", label: "" },
        ];
        return fields.filter(f => typeof f === "string" || !f.hidden);
    }

    /**
     * Helper text for the manual conversion factor. The direction matters: the value is the
     * multiplier applied to the formula quantity, not a divisor, and it stands in for the lot's COA.
     */
    get fixedFactorHelpText(): string {
        return "Enter the multiplier applied to the formula quantity to get the weigh-out (estradiol hemihydrate: 1.033). "
            + "When set, the selected lot's Certificate of Analysis is not applied for this ingredient — the two are never combined.";
    }

    /** The activity factor a fixed multiplier reports (its reciprocal), formatted; blank when none or invalid. */
    get fixedFactorPreview(): string {
        return formatActivityFactor(fixedFactorActivityFactor(this.upsertFixedConversionFactor), this.activityFactorSettings.decimalPlaces);
    }

    /**
     * A factor the server would refuse to apply. Blank is not invalid — it just means the ingredient
     * uses the lot's COA instead.
     */
    get fixedFactorInvalid(): boolean {
        return this.upsertFixedConversionFactor !== null && !isFixedFactorUsable(this.upsertFixedConversionFactor);
    }

    /**
     * The editor blocks the save on a factor outside the band, so this names the band and stops
     * there. The fill flow's extra clause — that the factor will be ignored and the weigh-out left
     * unadjusted — describes a factor already stored on a formula, which nothing here can reach.
     */
    get fixedFactorInvalidText(): string {
        return describeFixedFactorBand();
    }

    onFixedFactorInput(value: string | number | null) {
        this.upsertFixedConversionFactor = toNumberOrNull(value);
    }

    /**
     * Blank leaves the indicator unset, which lets the worksheet fall back to the GSDD-derived
     * value. Non-GSDD bulk chemicals have none, so those have to be set here.
     */
    private legendOptions = [
        { value: null, text: "(derive from drug)" },
        { value: "L", text: "L - Legend" },
        { value: "O", text: "O - OTC" },
    ]

    legendFormatter(value: string | null) {
        return value || "";
    }

    drugNameFormatter(value: number, _: string, item: CompoundIngredients) {
        const drugId = item.drugID;
        if (!drugId) return 'Invalid drug.';

        // Return cached value if available
        const cacheKey = `${item.drugSource}_${drugId}`;
        const cachedName = this.drugNamesCache.get(cacheKey);
        if (cachedName) return cachedName;

        // Load the name if not already loading
        if (!this.loadingDrugNames.has(cacheKey)) {
            this.loadDrugName(item);
        }

        return 'Loading...';
    }

    ndcFormatter(value: string, _: string, item: CompoundIngredients) {
        const drugId = item.drugID;
        if (!drugId) return '';

        const cacheKey = `${item.drugSource}_${drugId}`;
        const cachedNdc = this.ndcCache.get(cacheKey);
        if (cachedNdc !== undefined) return cachedNdc;

        // NDC is loaded alongside drug name in loadDrugName
        return 'Loading...';
    }

    async loadDrugName(item: CompoundIngredients) {
        const cacheKey = `${item.drugSource}_${item.drugID}`;
        this.loadingDrugNames.add(cacheKey);

        try {
            const response = await Axios.get<DrugProduct>(`/api/Drug/${item.drugID}`, {
                params: { drugSource: item.drugSource }
            });
            const drugName = response.data.productNameShort;
            this.drugNamesCache.set(cacheKey, drugName);
            item.drugName = drugName;

            const ndc = response.data.ndc || '';
            this.ndcCache.set(cacheKey, ndc);
            item.ndc = ndc;

            // Force table refresh to show the loaded name and NDC
            this.$nextTick(() => {
                if (this.ingredientsTable && this.ingredientsTable.refresh) {
                    this.ingredientsTable.refresh();
                }
            });
        } catch (err) {
            console.warn("Error loading drug name:", err);
            this.drugNamesCache.set(cacheKey, 'Error Getting Name');
            this.ndcCache.set(cacheKey, '');
            item.ndc = '';

            // Force table refresh to show the error
            this.$nextTick(() => {
                if (this.ingredientsTable && this.ingredientsTable.refresh) {
                    this.ingredientsTable.refresh();
                }
            });
        } finally {
            this.loadingDrugNames.delete(cacheKey);
        }
    }

    drugSourceFormatter(drugSource: DrugSource) {
        return DrugSource[drugSource].toString();
    }

    linkForItem(item: CompoundIngredients) {
        let name = '';
        if (item.drugSource == DrugSource.GsddFederal) name = 'Drug';
        if (item.drugSource == DrugSource.NonGsddProduct) name = 'Non-GSDD-Drug';
        return { name, params: { id: item.drugID } };
    }

    reset() {
        this.showForm = false;
        this.upsertId = 0;
        this.upsertDrugSource = 0;
        this.upsertDrugId = 0;
        this.upsertDrugName = "";
        this.upsertQuantity = 0;
        this.upsertUnitOfMeasure = "";
        this.upsertApplyActivityFactor = false;
        this.upsertFixedConversionFactor = null;
        this.upsertIngredientRow = new CompoundIngredients();
        this.alreadyShipped = false;
        console.log("created");
    }

    onContinue() {
        this.submitUpsert();
        this.$emit('continue');
    }

    submitUpsert() {
        if (this.upsertQuantity < 0.0001) {
            this.$bvModal.msgBoxOk('Quantity can not be zero or less.');
            return;
        }
        if (this.upsertApplyActivityFactor && this.fixedFactorInvalid) {
            // The same sentence the field itself shows, rather than a second rule stated only here:
            // "greater than 0" is neither the band this save is judged against nor the one the server
            // applies, so a factor of 0.3 was refused for failing a rule it already satisfied.
            this.$bvModal.msgBoxOk(`${this.fixedFactorInvalidText} Leave it blank to use the lot COA instead.`);
            return;
        }
        this.upsertIngredientRow = new CompoundIngredients();
        this.upsertIngredientRow.id = this.upsertId || 0;
        this.upsertIngredientRow.compoundID = this.compoundId;
        this.upsertIngredientRow.drugSource = this.upsertDrugSource || 0;
        this.upsertIngredientRow.drugID = this.upsertDrugId!;
        this.upsertIngredientRow.quantity = this.upsertQuantity!;
        this.upsertIngredientRow.unitOfMeasure = this.upsertUnitOfMeasure!;
        this.upsertIngredientRow.legendIndicator = this.upsertLegendIndicator;
        this.upsertIngredientRow.ingredientNote = this.upsertIngredientNote || null;
        // A fixed factor only means anything on a flagged ingredient; turning the flag off drops it so
        // a stale multiplier cannot silently come back when the flag is turned on again later.
        this.upsertIngredientRow.applyActivityFactor = this.upsertApplyActivityFactor;
        this.upsertIngredientRow.fixedConversionFactor = this.upsertApplyActivityFactor ? this.upsertFixedConversionFactor : null;

        const existingIndex = this.ingredients.map(item => item.id).indexOf(this.upsertIngredientRow.id);
        if (existingIndex > -1) {
            // Editing keeps the ingredient where it was; only the arrows change the order.
            this.upsertIngredientRow.order = this.ingredients[existingIndex].order;
            this.ingredients.splice(existingIndex, 1, this.upsertIngredientRow);
        } else {
            this.ingredients.push(this.upsertIngredientRow);
        }
        this.renumberIngredients();
        this.closeForm();
        this.$emit('continue');
    }

    /**
     * Moves an ingredient in the printed sequence. Order matters for geometric dilution, so it is
     * user-controlled rather than whatever the identity column happens to yield.
     */
    moveIngredient(item: CompoundIngredients, delta: number) {
        const index = this.ingredients.findIndex(i => i.id === item.id);
        const target = index + delta;
        if (index < 0 || target < 0 || target >= this.ingredients.length) return;

        this.ingredients.splice(target, 0, ...this.ingredients.splice(index, 1));
        this.renumberIngredients();
        this.$emit('continue');
    }

    private renumberIngredients() {
        this.ingredients.forEach((ingredient, index) => this.$set(ingredient, 'order', index + 1));
    }

    async updateIngredient(item: CompoundIngredients) {
        await this.onIngredientAddUpdDelClicked();
        if (!this.alreadyShipped) {
            this.showForm = true;
            this.upsertId = item.id;
            this.upsertDrugSource = item.drugSource;
            this.upsertDrugId = item.drugID;
            this.upsertQuantity = item.quantity;
            this.upsertUnitOfMeasure = item.unitOfMeasure;
            this.upsertLegendIndicator = item.legendIndicator ?? null;
            this.upsertIngredientNote = item.ingredientNote ?? "";
            this.upsertApplyActivityFactor = !!item.applyActivityFactor;
            this.upsertFixedConversionFactor = item.fixedConversionFactor ?? null;

            // Load drug name from cache or API
            const cacheKey = `${item.drugSource}_${item.drugID}`;
            const cachedName = this.drugNamesCache.get(cacheKey);
            if (cachedName) {
                this.upsertDrugName = cachedName;
            } else if (item.drugName) {
                this.upsertDrugName = item.drugName;
            } else {
                this.upsertDrugName = "Loading...";
                try {
                    const response = await Axios.get<DrugProduct>(`/api/Drug/${item.drugID}`, {
                        params: { drugSource: item.drugSource }
                    });
                    this.upsertDrugName = response.data.productNameShort;
                    this.drugNamesCache.set(cacheKey, response.data.productNameShort);
                } catch (err) {
                    console.warn("Error loading drug name:", err);
                    this.upsertDrugName = "Error Getting Name";
                }
            }
        } else this.closeForm();
    }

    async deleteConfirmDialog(item: CompoundIngredients) {
        await this.onIngredientAddUpdDelClicked();
        if (!this.alreadyShipped) {
            this.$bvModal.msgBoxConfirm(`This compound Ingredient will get deleted. Are you sure?`, {
                title: 'Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'YES',
                cancelTitle: 'NO',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
                .then(value => {
                    if (!value) return;
                    this.deleteIngredient(item);
                })
                .catch(err => {
                    console.error("Error caught on Delete button.", err);
                });
        } else this.closeForm();
    }

    deleteIngredient(item: CompoundIngredients) {
        if (!item) return;

        const itemIdType = typeof (item.id);
        if (itemIdType != "number") return;

        if (this.ingredients.map(item => item.id).indexOf(item.id) < 0) return;
        this.ingredients.splice(this.ingredients.map(ci => ci.id).indexOf(item.id), 1);
        this.$emit('delete');
    }

    async onNewIngredient() {
        await this.onIngredientAddUpdDelClicked();
        this.clearForm();
        if (!this.alreadyShipped) this.$bvModal.show("IngredientDrugSelect");
    }

    openFrom() {
        this.showForm = true;
    }

    closeForm() {
        this.clearForm();
        this.showForm = false;
    }

    clearForm() {
        this.upsertId = Math.min(...this.ingredients.map(item => item.id)) - 1;
        if (this.upsertId > 0) this.upsertId = 0;
        this.upsertDrugSource = 0;
        this.upsertDrugId = 0;
        this.upsertDrugName = "";
        this.upsertQuantity = 0;
        this.upsertUnitOfMeasure = ""
        this.upsertLegendIndicator = null;
        this.upsertIngredientNote = "";
        this.upsertApplyActivityFactor = false;
        this.upsertFixedConversionFactor = null;
    }

    onCancelClick() {
        this.closeForm();
        this.$emit('cancel');
    }

    async onIngredientSelected(parm1: any) {
        //console.log(parm1);
        var drugSrcId = parm1.split("_", 4)
        this.upsertDrugSource = Number(drugSrcId[0]);
        this.upsertDrugId = Number(drugSrcId[1]);
        this.upsertQuantity = Number(drugSrcId[2]);
        this.upsertUnitOfMeasure = drugSrcId[3];
        this.$bvModal.hide("IngredientDrugSelect");
        var duplicate = this.ingredients.some(obj => obj.drugSource == this.upsertDrugSource && obj.drugID == this.upsertDrugId)
        if (duplicate) {
            this.$bvModal.msgBoxOk('Duplicate DrugSource/DrugId selected.');
            return;
        }

        // Load drug name
        const cacheKey = `${this.upsertDrugSource}_${this.upsertDrugId}`;
        const cachedName = this.drugNamesCache.get(cacheKey);
        if (cachedName) {
            this.upsertDrugName = cachedName;
        } else {
            this.upsertDrugName = "Loading...";
            try {
                const response = await Axios.get<DrugProduct>(`/api/Drug/${this.upsertDrugId}`, {
                    params: { drugSource: this.upsertDrugSource }
                });
                this.upsertDrugName = response.data.productNameShort;
                this.drugNamesCache.set(cacheKey, response.data.productNameShort);
                this.ndcCache.set(cacheKey, response.data.ndc || '');
            } catch (err) {
                console.warn("Error loading drug name:", err);
                this.upsertDrugName = "Error Getting Name";
            }
        }

        this.submitUpsert();
        this.$emit('continue');
    }

    onIngredientCanceled() {
        this.$bvModal.hide("IngredientDrugSelect");
    }

    async onIngredientAddUpdDelClicked() {
        let shippedSearchResults: number | null = null;
        let sc: number = 0;
        let scGood: number[] = [200, 204];
        await this.fetchShippedSearch(this.compoundId!)
            .then(value => {
                if (value.length == null) sc = value;
                else {
                    sc = value[0];
                    shippedSearchResults = value[1];
                }
            });
        if (!scGood.includes(sc)) {
            this.$bvModal.msgBoxOk(`Database error occurred while attempting to search if compound has a prescription shipped`);
            return false;
        }
        if (shippedSearchResults != null && shippedSearchResults != "") {
            this.$bvModal.msgBoxOk(`Ingredients can't be changed as compound already has a shipped prescription`);
            this.alreadyShipped = true;
        } else this.alreadyShipped = false;
    }

    async fetchShippedSearch(compoundId: number): Promise<[number, number | null] | any> {
        try {
            const response = await this.$http.get(`/Compound/prescription-shipped-search?compoundid=${compoundId.toString()}`);
            const shippedFound: number | null = response.data;
            const sc: number = Number(response.status);
            return [sc, shippedFound];
        } catch (err) {
            const error = err as AxiosError;
            console.error('Error while searching if compound has a shipped prescription order.', {
                error,
                response: error?.response
            });
            this.$notification(NotificationOptions.error(err));
            return error.response?.status;
        }
    }

}
</script>

<style scoped>
/* Bootstrap gives `.disabled` meaning only on .btn/.nav-link, so a bare anchor carrying the class
   still takes clicks and still looks enabled -- the reorder arrows at the ends of the list looked
   disabled but weren't. The move handler's bounds check already no-ops them; this makes the
   affordance honest rather than relying on that guard. */
a.disabled {
    pointer-events: none;
    opacity: 0.4;
}
</style>
